import React from "react";
import { ShieldPlus, Sparkles, Info } from "lucide-react";

/* ==================== Protection Add-Ons Card (Feb 2026) ====================
 * Replaces the legacy $20 flat Damage Protection with two granular add-ons:
 *
 *   1. Enchanted Care  — 8% of subtotal ($5 min), non-refundable damage waiver.
 *   2. Enchanted Peace of Mind — 20% of subtotal, cancel-anytime coverage.
 *      Only offered when the trip is booked ≥ 7 days ahead of delivery.
 *
 * Both are pre-checked by default. The parent CheckoutForm owns the boolean
 * state and pushes toggles to /checkout/update.
 */
export const ProtectionAddOnsCard = ({
  careFee,
  careChecked,
  onToggleCare,
  pomFee,
  pomChecked,
  onTogglePom,
  pomEligible,
  pomMinDaysOut = 7,
  onLearnMoreCare,
  onLearnMorePom,
}) => {
  return (
    <div className="glass p-5 sm:p-6 space-y-4" data-testid="protection-addons-card">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-[#FFD166]" />
        <h3 className="font-serif text-xl text-[#FFD166]">Protection add-ons</h3>
      </div>

      {/* -------- Enchanted Care -------- */}
      <label
        className={`flex items-start gap-3 cursor-pointer border-2 rounded-xl p-4 transition-colors ${
          careChecked ? "border-emerald-500/50 bg-emerald-500/5" : "border-[#FFD166]/15 bg-black/25"
        }`}
        data-testid="enchanted-care-wrap"
      >
        <input
          type="checkbox"
          checked={careChecked}
          onChange={(e) => onToggleCare(e.target.checked)}
          className="mt-1 w-5 h-5 accent-[#FFD166] shrink-0"
          data-testid="enchanted-care-checkbox"
        />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <ShieldPlus className="w-4 h-4 text-[#FFD166] shrink-0" />
            <span className="font-medium text-[#F6F1E4]">Enchanted Care</span>
            <span className="text-[#FFD166] font-serif text-lg" data-testid="enchanted-care-fee">
              +${Number(careFee || 0).toFixed(2)}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-[#B9C8D5]">8% · $5 minimum · refundable if cancelled before delivery</span>
          </div>
          <p className="text-[11px] text-[#B9C8D5] mt-1 leading-relaxed">
            Covers minor accidental damage during your rental so a spilled bottle doesn't become a repair bill.{" "}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); onLearnMoreCare(); }}
              className="underline text-[#FFD166] hover:text-[#F4A93A]"
              data-testid="enchanted-care-learn-more"
            >
              What's covered?
            </button>
          </p>
        </div>
      </label>

      {/* -------- Enchanted Peace of Mind -------- */}
      {pomEligible ? (
        <label
          className={`flex items-start gap-3 cursor-pointer border-2 rounded-xl p-4 transition-colors ${
            pomChecked ? "border-[#FFD166]/60 bg-[#FFD166]/8" : "border-[#FFD166]/15 bg-black/25"
          }`}
          data-testid="enchanted-pom-wrap"
        >
          <input
            type="checkbox"
            checked={pomChecked}
            onChange={(e) => onTogglePom(e.target.checked)}
            className="mt-1 w-5 h-5 accent-[#FFD166] shrink-0"
            data-testid="enchanted-pom-checkbox"
          />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#FFD166] shrink-0" />
              <span className="font-medium text-[#F6F1E4]">Enchanted Peace of Mind</span>
              <span className="text-[#FFD166] font-serif text-lg" data-testid="enchanted-pom-fee">
                +${Number(pomFee || 0).toFixed(2)}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-[#B9C8D5]">20% · cancel anytime · fee non-refundable</span>
            </div>
            <p className="text-[11px] text-[#B9C8D5] mt-1 leading-relaxed">
              Cancel any time before delivery for a 100 % rental refund. The 20 % Peace of Mind fee itself is non-refundable.{" "}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); onLearnMorePom(); }}
                className="underline text-[#FFD166] hover:text-[#F4A93A]"
                data-testid="enchanted-pom-learn-more"
              >
                How it works
              </button>
            </p>
          </div>
        </label>
      ) : (
        <div
          className="flex items-start gap-3 border-2 border-dashed border-[#FFD166]/20 rounded-xl p-4 bg-black/20"
          data-testid="enchanted-pom-locked"
        >
          <Info className="w-4 h-4 text-[#B9C8D5] mt-1 shrink-0" />
          <div className="flex-1 text-[11px] text-[#B9C8D5] leading-relaxed">
            <span className="text-[#F6F1E4] font-medium">Enchanted Peace of Mind</span> is only offered when
            you book at least <span className="text-[#FFD166]">{pomMinDaysOut}&nbsp;days</span> ahead of your delivery date.
            Standard cancellation policy still applies to this trip.
          </div>
        </div>
      )}
    </div>
  );
};

export default ProtectionAddOnsCard;
